import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Section, Field, ErrorBox, Empty, RevisionSelect, StatusPill, inputCls, errMsg, fmtDate } from "./shared";

const CHECKS = [
  ["brief_reviewed", "Product brief reviewed"],
  ["formula_reviewed", "Formula for this revision reviewed"],
  ["evidence_reviewed", "Analytical and sensory evidence reviewed"],
];

export default function DecisionsPanel({ recipeId, recipe, revisions, decisions, stages, tests, onReload }) {
  const [revId, setRevId] = useState(recipe.current_revision_id || revisions[0]?.id || "");
  const [form, setForm] = useState({ stage: "", decision: "hold", rationale: "", checks: {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const revDecisions = decisions.filter((d) => d.revision_id === revId);
  const latestByStage = {};
  [...revDecisions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).forEach((d) => { latestByStage[d.stage] = d; });
  const blocking = tests.filter((t) => t.revision_id === revId && !t.voided && (t.status === "fail" || t.status === "unassessed"));
  const allChecked = CHECKS.every(([k]) => form.checks[k]);
  const rev = revisions.find((r) => r.id === revId);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      await api.post(`/rd/recipes/${recipeId}/decisions`, {
        revision_id: revId, stage: form.stage, decision: form.decision, rationale: form.rationale,
        checks: Object.fromEntries(CHECKS.map(([k]) => [k, !!form.checks[k]])),
      });
      toast.success("Decision recorded");
      setForm({ stage: "", decision: "hold", rationale: "", checks: {} });
      onReload();
    } catch (err) { setError(`${errMsg(err)} Your entry is preserved.`); }
    finally { setSaving(false); }
  };

  return (
    <Section icon={SealCheck} title="Stage-gate decisions" testId="rd-decisions"
      subtitle="Decisions are append-only and bound to one revision. Approvals never carry forward to newer revisions. The approver is recorded from your signed-in account.">
      {revisions.length === 0 ? <Empty>Save a formula revision before recording decisions.</Empty> : (
        <>
          <div className="w-full sm:w-96 mb-4"><RevisionSelect revisions={revisions} value={revId} onChange={setRevId} testId="select-decision-revision" /></div>
          {revId !== recipe.current_revision_id && (
            <div className="mb-3 text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2">Viewing Rev {rev?.number}, which is not the current revision. Decisions here do not apply to the current formula.</div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-5">
            {stages.map((s, i) => {
              const d = latestByStage[s.id];
              return (
                <div key={s.id} className="rounded-md border border-stone-200 px-3 py-2" data-testid={`stage-${s.id}`}>
                  <div className="text-[10px] font-mono text-stone-400">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm font-semibold text-stone-800 leading-tight">{s.label}</div>
                  <div className="mt-1 flex gap-1 flex-wrap">{d ? <><span className={d.stale ? "opacity-50 line-through" : ""}><StatusPill status={d.decision} /></span>{d.stale && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800" data-testid={`status-stale-${s.id}`}>stale — re-review</span>}</> : <span className="text-[10px] uppercase tracking-wider text-stone-400">no decision</span>}</div>
                  {d && <div className="text-[10px] text-stone-500 mt-0.5">{d.decided_by} · {fmtDate(d.created_at)}</div>}
                </div>
              );
            })}
          </div>

          <form onSubmit={submit} className="rounded-md border border-stone-200 bg-stone-50/60 p-4 space-y-3 mb-5" data-testid="form-decision">
            <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500">Record decision for Rev {rev?.number}</div>
            <ErrorBox message={error} testId="error-decision" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Stage">
                <select required className={inputCls} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="select-decision-stage">
                  <option value="">Select stage…</option>
                  {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Decision">
                <select className={inputCls} value={form.decision} onChange={(e) => setForm({ ...form, decision: e.target.value })} data-testid="select-decision-value">
                  <option value="pass">Pass</option><option value="hold">Hold</option><option value="fail">Fail</option>
                </select>
              </Field>
            </div>
            <div className="space-y-1">
              {CHECKS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-stone-700">
                  <input type="checkbox" checked={!!form.checks[k]} onChange={(e) => setForm({ ...form, checks: { ...form.checks, [k]: e.target.checked } })} data-testid={`checkbox-${k}`} /> {label}
                </label>
              ))}
            </div>
            <Field label="Rationale"><textarea required rows={3} className={inputCls} value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} data-testid="input-decision-rationale" /></Field>
            {form.decision === "pass" && (!allChecked || blocking.length > 0) && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                {!allChecked && <div>Pass requires all three review checks.</div>}
                {blocking.length > 0 && <div>{blocking.length} active failed or unassessed analytical record(s) on this revision will block a pass. Void incorrect records or add targets.</div>}
                <div>Later stages also require the prior stage passed on this same revision.</div>
              </div>
            )}
            <div className="flex justify-end">
              <Button type="submit" disabled={saving || !form.stage || !form.rationale.trim()} data-testid="button-submit-decision">{saving ? "Recording…" : "Record decision"}</Button>
            </div>
          </form>

          <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-2">Decision log — Rev {rev?.number}</div>
          {revDecisions.length === 0 ? <Empty>No decisions recorded for this revision.</Empty> : (
            <div className="space-y-2">
              {[...revDecisions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((d) => (
                <div key={d.id} className="rounded-md border border-stone-200 px-3 py-2" data-testid={`row-decision-${d.id}`}>
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <span className={d.stale ? "opacity-50 line-through" : ""}><StatusPill status={d.decision} /></span>
                    {d.stale && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-800" data-testid={`status-decision-stale-${d.id}`}>stale — evidence or brief changed since this decision; not a current approval</span>}
                    <span className="font-semibold text-stone-800">{stages.find((s) => s.id === d.stage)?.label || d.stage}</span>
                    <span className="text-xs text-stone-500">{d.decided_by} · {fmtDate(d.created_at)}</span>
                  </div>
                  <p className="text-sm text-stone-700 mt-1 whitespace-pre-wrap">{d.rationale}</p>
                  <div className="text-[11px] text-stone-500 mt-1">
                    {CHECKS.map(([k, label]) => <span key={k} className="mr-3">{d.checks?.[k] ? "[x]" : "[ ]"} {label}</span>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
